import { Card, CardContent } from "../../ui/card";
import { motion } from "framer-motion";
import { SummaryHeader } from "../../ui/SummaryHeader";
import { cn } from "../../../utils/utils";
import { useEcommSales } from "../hooks/useSalesEcomm";
import DataLoader from "../../../components/loaders/DataLoader";
import { endOfMonth, format, startOfMonth } from "date-fns";
import { useState, useMemo, useCallback } from "react";
import { formatCurrency } from "../../../utils/formatCurrency";
import { MonthYearPicker } from "../../ui/month-Year-Picker";

const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "backOut" } },
};

interface TopProductsProps {
  selectedMonth: string;
}

interface ProductData {
  style: string;
  title: string;
  image_src?: string;
  average_price: number;
  revenue: number;
  units_sold: number;
}

export function EcommShopifyTopProducts({ selectedMonth }: TopProductsProps) {
  const now = new Date();
  const [search, setSearch] = useState("");
  // initialize picker from incoming selectedMonth (if provided) otherwise current month
  const initialDate = selectedMonth ? (() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    return new Date(year, month - 1, 1); // month - 1 because Date months are 0-indexed but yyyy-MM string is 1-indexed
  })() : new Date(now.getFullYear(), now.getMonth());
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(initialDate);

  // derive api start/end from the picker (MonthYearPicker)
  const { startDate, endDate } = useMemo(() => {
    const monthDate = selectedDate ?? new Date(now.getFullYear(), now.getMonth());
    return {
      startDate: format(startOfMonth(monthDate), "yyyy-MM-dd"),
      endDate: format(endOfMonth(monthDate), "yyyy-MM-dd"),
    };
  }, [selectedDate, now]);

  const { data, isLoading } = useEcommSales(startDate, endDate);

  // Memoize filtered products to avoid recalculation on every render
  const filteredProducts = useMemo(() => {
    if (!data?.shopify_top_products_performance) return [];
    
    if (!search.trim()) return data.shopify_top_products_performance;
    
    const searchLower = search.toLowerCase().trim();
    return data.shopify_top_products_performance.filter((item: ProductData) =>
      item.title.toLowerCase().includes(searchLower)
    );
  }, [data?.shopify_top_products_performance, search]);

  // Use useCallback to prevent unnecessary re-renders
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);


  // Helper function to get sales status styling
  const getSalesStatusClass = useCallback((unitsSold: number) => {
    return cn(
      "rounded-md px-1.5 py-0.5 text-xs whitespace-nowrap font-medium",
      unitsSold > 0
        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
        : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
    );
  }, []);

  return (
    <div className="flex flex-col h-full">
      <SummaryHeader title="Top Products" />
      <Card className="flex-1 flex flex-col">
        <div className="flex items-center justify-between m-4">
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search by product name..."
              className="w-[250px] border border-border rounded-md h-9 px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
              aria-label="Search products"
            />
          </div> 
          <div className="pr-3"><MonthYearPicker selected={selectedDate} onChange={setSelectedDate} /></div>
        </div>
        
        <CardContent className="flex-1 flex flex-col p-0">
          {isLoading ? (
            <div className="p-4">
              <DataLoader variant="top-products" itemCount={6} />
            </div>
          ) : (
            <div className="flex-1 max-h-[400px] overflow-y-auto">
              {filteredProducts.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-muted-foreground">
                  {search ? 'No products found matching your search.' : 'No products available.'}
                </div>
              ) : (
                <motion.div
                  variants={container}
                  initial="hidden"  
                  animate="show"
                  className="p-4 space-y-3"
                >
                  {filteredProducts.map((productData: ProductData) => (
                    <motion.div
                      key={productData.style}
                      variants={item}
                      className="rounded-lg flex items-center justify-between gap-4 p-3 transition-all duration-200 hover:bg-accent hover:scale-[1.01] border border-transparent hover:border-border"
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="relative flex-shrink-0">
                          <img
                            src={productData.image_src ?? "/T-Logo.png"}
                            alt={`Product image for ${productData.title}`}
                            className="h-12 w-12 object-cover rounded-md shadow-sm border border-border"
                            loading="lazy"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = "/T-Logo.png";
                            }}
                          />
                        </div>
                        
                        <div className="space-y-1 min-w-0 flex-1">
                          <div className="font-medium text-sm leading-tight line-clamp-2 text-foreground">
                            {productData.title}
                          </div>
                          <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
                            <span>Price: {formatCurrency(productData.average_price)}</span>
                            <span>Revenue: {formatCurrency(productData.revenue)}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex-shrink-0">
                        <div className={getSalesStatusClass(productData.units_sold)}>
                          Sold: {productData.units_sold.toLocaleString()}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}